import { InteractionRequiredAuthError } from '@azure/msal-browser'
import type React from 'react'
import { useState, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import SearchInput from '@/components/input/SearchInput'
import Subjects from '@/components/LegacySubjectsList'
import PageContainer from '@/components/PageContainer'
import SearchResults from '@/components/SearchResults'
import ctx from '@/context/AppContext'
import useUser from '@/hooks/useUser'
import { createVouchApiService } from '@/lib/vouchApiService'
import logger from '@/lib/vouchLogger'
import type { UserData } from '@/types/User'
import { getAccessToken } from '@/utils/authentication'

import NonSMEPage from './NonSMEHomePage'

interface Props {
  userData: UserData
  token: string
  setCollapsedMenu: Function
}

const HomeContainer = styled.div`
  display: grid;
  grid-template-rows: min-content 1fr;
  row-gap: 10px;
  height: 100%;
`

const PageBody = styled.div`
  display: grid;
  min-height: 0;
`

const HomePage: React.FC<Props> = ({ setCollapsedMenu }) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const { customerId, personId } = useContext(ctx)

  const { skillLevel } = useUser()

  useEffect(() => {
    if (!personId) {
      return
    }

    // This is a temporary experiment to validate the JWT token, so we can log and see when it expired
    async function validateJwtExperiment(userId: string) {
      const vouchApiService = createVouchApiService(logger, getAccessToken)

      try {
        await vouchApiService.jwtValidator({
          userId,
        })

        logger.info('Validated JWT', { personId })
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          return
        }

        logger.warning('Failed to validate JWT - Unexpected', { personId })
      }
    }

    validateJwtExperiment(personId)
  }, [personId])

  const history = useHistory()

  function clearSearch() {
    setSearchTerm('')
  }

  function isNotSME(skillLevel: number) {
    return skillLevel < 6
  }

  async function setApplicant(subjectId: string) {
    setCollapsedMenu(true)
    history.push('/applicant/' + subjectId)
  }

  async function auxSetApplicant(subjectId: string) {
    const targetURL = '/applicant/' + subjectId
    window.open(targetURL, '_blank')?.focus()
  }

  const currentCustomerId = customerId

  if (currentCustomerId === undefined) {
    return <></>
  }

  return (
    <PageContainer>
      <HomeContainer>
        <SearchInput
          label="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <PageBody>
          {searchTerm.trim() !== '' ? (
            <SearchResults
              onApplicantSelected={setApplicant}
              onApplicantAuxSelected={auxSetApplicant}
              searchTerm={searchTerm}
              clearSearch={clearSearch}
            />
          ) : isNotSME(skillLevel) ? (
            <NonSMEPage />
          ) : (
            <Subjects
              onApplicantSelected={setApplicant}
              onApplicantAuxSelected={auxSetApplicant}
            />
          )}
        </PageBody>
      </HomeContainer>
    </PageContainer>
  )
}

export default HomePage
